import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client } from "@/api"

export type WelcomeOfferStage =
  "shown" | "dismissed" | "accepted" | "details_received" | "onboarded" | "cold"
export type WelcomeOfferQueueFilter = "accepted" | "details_received" | "onboarded" | "cold" | "all"
export const welcomeOfferStageLabels: Record<WelcomeOfferStage, string> = {
  shown: "Shown",
  dismissed: "Dismissed",
  accepted: "Accepted",
  details_received: "Details received",
  onboarded: "Onboarded",
  cold: "Cold",
}

export function useWelcomeOffers(stage: WelcomeOfferQueueFilter, search: string, page: number) {
  return useQuery({
    queryKey: ["admin", "welcome-offers", { stage, search, page }],
    queryFn: async () => {
      const response = await client.admin["welcome-offers"].$get({
        query: { stage, search, limit: "25", offset: String(page * 25) },
      })
      if (!response.ok) throw new Error("Could not load welcome-offer requests")
      return response.json()
    },
  })
}

export type WelcomeOfferSettings = { paused: boolean; env_enabled: boolean }

export function useWelcomeOfferSettings() {
  return useQuery<WelcomeOfferSettings>({
    queryKey: ["admin", "welcome-offers", "settings"],
    queryFn: async () => {
      const response = await client.admin["welcome-offers"].settings.$get()
      if (!response.ok) throw new Error("Could not load welcome-offer settings")
      return response.json()
    },
  })
}

export function useUpdateWelcomeOfferSettings() {
  const queryClient = useQueryClient()
  return useMutation<WelcomeOfferSettings, Error, { paused: boolean }>({
    mutationFn: async ({ paused }) => {
      const response = await client.admin["welcome-offers"].settings.$put({ json: { paused } })
      if (!response.ok) throw new Error("Could not update welcome-offer settings")
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["admin", "welcome-offers", "settings"], data)
    },
  })
}
